import type { CompletionMetricsSummary, CompletionMonthlyTraffic } from 'types';
import { CHANNEL_ROWS, MONTH_NAMES } from './CompletionMetrics.consts';

export type NumberSeparators = { decimalSeparator: string; thousandSeparator: string };

// Browser ICU data for 'is' is inconsistent ("4209" / "4 209"), so separators come from app state.
export function createFormatters({ decimalSeparator, thousandSeparator }: NumberSeparators) {
	const localize = (value: number | null | undefined, fractionDigits: number) => {
		const [int, frac] = (value ?? 0).toFixed(fractionDigits).split('.');
		const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
		return frac ? `${grouped}${decimalSeparator}${frac}` : grouped;
	};
	const formatCount = (value: number) => localize(value, 0);
	const formatDecimal = (value: number | null | undefined) => localize(value, 2);
	const formatPercent = (value: number | null | undefined) => `${formatDecimal(value)}%`;
	return { formatCount, formatDecimal, formatPercent };
}

export type Formatters = ReturnType<typeof createFormatters>;

export function formatUtcDate(iso: string) {
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return '—';
	const day = String(date.getUTCDate()).padStart(2, '0');
	const month = String(date.getUTCMonth() + 1).padStart(2, '0');
	return `${day}.${month}.${date.getUTCFullYear()}`;
}

export function formatUtcDateTime(iso: string) {
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return '—';
	const hours = String(date.getUTCHours()).padStart(2, '0');
	const minutes = String(date.getUTCMinutes()).padStart(2, '0');
	return `${formatUtcDate(iso)}, ${hours}:${minutes} UTC`;
}

export function monthLabel(iso: string, shortYear: boolean) {
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return '—';
	const year = date.getUTCFullYear();
	return `${MONTH_NAMES[date.getUTCMonth()]} ${shortYear ? `’${String(year).slice(2)}` : year}`;
}

// Drop-off is not returned by the API; derived per story definition.
export function dropOffPercent(summary: CompletionMetricsSummary) {
	if (!summary.totalProcesses) return 0;
	return ((summary.canceled50Count + summary.expired60Count) / summary.totalProcesses) * 100;
}

export function channelPieGradient(summary: CompletionMetricsSummary) {
	let acc = 0;
	const stops: string[] = [];
	for (const channel of CHANNEL_ROWS) {
		const from = acc * 3.6;
		acc += Math.max(0, channel.percent(summary));
		stops.push(`${channel.color} ${from.toFixed(2)}deg ${(acc * 3.6).toFixed(2)}deg`);
	}
	if (acc <= 0) return '#f4f4f5';
	stops.push(`#f4f4f5 ${(acc * 3.6).toFixed(2)}deg 360deg`);
	return `conic-gradient(${stops.join(',')})`;
}

export type TrafficBar = {
	completedHeight: string;
	createdHeight: string;
	key: string;
	rateHeight: string;
	rateTitle: string;
	title: string;
};

export function buildTrafficBars(
	rows: CompletionMonthlyTraffic[],
	separators: NumberSeparators,
	t: (key: string) => string
): TrafficBar[] {
	const { formatCount, formatPercent } = createFormatters(separators);
	const max = Math.max(1, ...rows.map(row => row.createdCount));
	const shortYear = rows.length > 24;
	return rows.map(row => {
		const label = monthLabel(row.metricDateUtc, shortYear);
		const rate = Math.min(100, Math.max(0, row.completedPercentExcludingCanceledExpired ?? 0));
		const completedShare = row.createdCount ? (row.completedCount / row.createdCount) * 100 : 0;
		return {
			key: row.metricDateUtc,
			createdHeight: `${Math.max(2, (row.createdCount / max) * 100)}%`,
			completedHeight: `${row.completedCount ? Math.max(6, completedShare) : 0}%`,
			rateHeight: `${rate > 0 ? Math.max(3, rate) : 0}%`,
			title: `${label} — ${t('created')} ${formatCount(row.createdCount)}, ${t('completed').toLowerCase()} ${formatCount(row.completedCount)}`,
			rateTitle: `${label} — ${formatPercent(row.completedPercentExcludingCanceledExpired)} ${t('of_active_completed')}`
		};
	});
}

export type AxisTick = { key: string; left: string; label: string };

export function buildAxisTicks(rows: CompletionMonthlyTraffic[]): AxisTick[] {
	const ticks: AxisTick[] = [];
	rows.forEach((row, index) => {
		const date = new Date(row.metricDateUtc);
		if (Number.isNaN(date.getTime())) return;
		const left = `${((index + 0.5) / rows.length) * 100}%`;
		if (rows.length > 24) {
			if (date.getUTCMonth() === 0 || index === 0) {
				ticks.push({ key: row.metricDateUtc, left, label: String(date.getUTCFullYear()) });
			}
		} else if (rows.length <= 12 || index % 2 === 0) {
			ticks.push({ key: row.metricDateUtc, left, label: MONTH_NAMES[date.getUTCMonth()] });
		}
	});
	return ticks;
}
